import React, { ReactElement } from 'react';
import isNil from 'lodash/isNil';
import { ICellRendererFunc } from 'ag-grid-community/dist/lib/rendering/cellRenderers/iCellRenderer';
import { ICellRendererParams } from 'ag-grid-community';
import classNames from 'classnames';
import { GridDetailsPanelFieldDef } from 'utils/components/GridDetailsPanel/gridDetailsPanelColumnDef';
import { abbreviate } from 'utils/abbreviations';
import { GridDetailColDef } from 'utils/components/GridDetailsPanel/GridDetailsPanel';

export interface GridDetailsPanelFieldProps<
    TColumnId extends string,
    TEntity extends Partial<Record<TColumnId, unknown>>,
> {
    colDefs: GridDetailColDef<TEntity>[];
    control: GridDetailsPanelFieldDef<TColumnId>;
    entity: TEntity;
}

export function GridDetailsPanelField<
    TColumnId extends string,
    TEntity extends Partial<Record<TColumnId, unknown>>,
>(props: GridDetailsPanelFieldProps<TColumnId, TEntity>) {
    const { colDefs, control, entity } = props;

    let label: string = control.field;
    const value = entity[control.field];

    const colDef = colDefs.find((d) => d.field === control.field)!;

    const forceShow = colDef.showDetailValue && colDef.showDetailValue(entity);

    if (isNil(value) && !control.showWhenNil && !forceShow) {
        return <></>;
    }

    let displayValue: string | ReactElement = isNil(value)
        ? ''
        : (value as string).toString();

    label = colDef.headerName ?? '';

    if (colDef!.cellRenderer) {
        const renderer = colDef!.cellRenderer as ICellRendererFunc;
        const params = colDef!.cellRendererParams;
        const rendered = renderer({
            value,
            data: entity,
            ...params,
        } as ICellRendererParams);
        if (typeof rendered === 'string') {
            displayValue = (
                <span
                    /* eslint-disable-next-line @typescript-eslint/naming-convention */
                    dangerouslySetInnerHTML={{ __html: rendered as string }}
                />
            );
        }
        if (React.isValidElement(rendered)) {
            displayValue = rendered;
        }
    }

    const cssClasses = classNames(
        'field',
        `field-${control.field}`,
        control.hideLabel && 'hidden-label'
    );

    const labelDisplay = abbreviate(label);
    const labelTitle = labelDisplay === label ? '' : label;

    return (
        <div className={cssClasses}>
            {!control.hideLabel && (
                <div className="label" title={labelTitle}>
                    {labelDisplay}:
                </div>
            )}
            <div
                className="value"
                title={
                    typeof displayValue === 'string' ? displayValue : undefined
                }
            >
                {displayValue}
            </div>
        </div>
    );
}
